// 颜色空间转换与最近色匹配（基于 Oklab，更贴合人眼感知）

export function hexToRgb(hex) {
  const clean = hex.replace('#', '')
  const bigint = parseInt(clean, 16)
  return {
    r: (bigint >> 16) & 255,
    g: (bigint >> 8) & 255,
    b: bigint & 255,
  }
}

export function rgbToHex(r, g, b) {
  return '#' + [r, g, b].map((v) => v.toString(16).padStart(2, '0')).join('')
}

// sRGB -> Linear RGB
function srgbToLinear(c) {
  const v = c / 255
  return v <= 0.04045 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4)
}

// Linear RGB -> XYZ (D65)
function linearRgbToXyz(lr, lg, lb) {
  return {
    x: lr * 0.4124564 + lg * 0.3575761 + lb * 0.1804375,
    y: lr * 0.2126729 + lg * 0.7151522 + lb * 0.0721750,
    z: lr * 0.0193339 + lg * 0.1191920 + lb * 0.9503041,
  }
}

// XYZ -> Oklab
// 参考：https://bottosson.github.io/posts/oklab/
export function xyzToOklab(x, y, z) {
  const l = 0.8190224432164319 * x + 0.3619062562801221 * y - 0.1288737826121641 * z
  const m = 0.0329836671980271 * x + 0.9292868468965546 * y + 0.0361446681699984 * z
  const s = 0.0481771995664632 * x + 0.2642395249442276 * y + 0.6335478259696937 * z

  const l_ = Math.cbrt(l)
  const m_ = Math.cbrt(m)
  const s_ = Math.cbrt(s)

  return {
    L: 0.2104542553 * l_ + 0.7936177850 * m_ - 0.0040720468 * s_,
    a: 1.9779984951 * l_ - 2.4285922050 * m_ + 0.4505937099 * s_,
    b: 0.0259040371 * l_ + 0.7827717662 * m_ - 0.8086757660 * s_,
  }
}

export function rgbToOklab(r, g, b) {
  const lr = srgbToLinear(r)
  const lg = srgbToLinear(g)
  const lb = srgbToLinear(b)
  const xyz = linearRgbToXyz(lr, lg, lb)
  return xyzToOklab(xyz.x, xyz.y, xyz.z)
}

export function oklabDistance(ok1, ok2) {
  return Math.sqrt(
    Math.pow(ok1.L - ok2.L, 2) +
    Math.pow(ok1.a - ok2.a, 2) +
    Math.pow(ok1.b - ok2.b, 2)
  )
}

export const colorDistance = oklabDistance

// 缓存品牌色 Oklab
const oklabCache = new Map()

export function getBrandOklabColors(brandColors) {
  const key = brandColors.map((c) => `${c.id}:${c.hex}`).join('|')
  if (oklabCache.has(key)) return oklabCache.get(key)
  const result = brandColors.map((c) => {
    const rgb = hexToRgb(c.hex)
    return { ...c, oklab: rgbToOklab(rgb.r, rgb.g, rgb.b) }
  })
  oklabCache.set(key, result)
  return result
}

export const getBrandLabColors = getBrandOklabColors

export function findNearestColor(r, g, b, brandColors) {
  // 黑/白偏好：仅吸附接近纯黑/纯白的像素（如描边、高光、纯白背景），
  // 阈值必须足够小，否则肤色、浅色系会被错误量化成白，深色被量化成黑
  const luma = 0.299 * r + 0.587 * g + 0.114 * b
  const sum = r + g + b
  const blackColor = brandColors.find((c) => c.hex?.toLowerCase() === '#000000')
  const whiteColor = brandColors.find((c) => c.hex?.toLowerCase() === '#ffffff')

  if (blackColor && (luma < 60 || sum < 180)) {
    const d = r * r + g * g + b * b
    if (d < 625) return blackColor // 25^2，仅接近纯黑
  }

  if (whiteColor && (luma > 200 || sum > 600)) {
    const d = (255 - r) ** 2 + (255 - g) ** 2 + (255 - b) ** 2
    if (d < 625) return whiteColor // 25^2，仅接近纯白
  }

  const target = rgbToOklab(r, g, b)
  const palette = getBrandOklabColors(brandColors)
  let best = palette[0]
  let bestDist = Infinity
  for (const color of palette) {
    const d = oklabDistance(target, color.oklab)
    if (d < bestDist) {
      bestDist = d
      best = color
    }
  }
  return best
}
